import {Component, DestroyRef, OnInit, ViewChild} from '@angular/core';
import {PLAN_VALIDATION_STAGE, RULE_OUTCOME, TABS} from '../common/constants';
import {TenureInterestsNotificationsComponent} from './tenure-interests-notifications/tenure-interests-notifications.component';
import {NewInterestsNotificationsComponent} from './new-interests-notifications/new-interests-notifications.component';
// eslint-disable-next-line max-len
import {AdditionalInterestNotificationQuestionsComponent} from './additional-interest-notification-questions/additional-interest-notification-questions.component';
import {LookupService} from '../common/service/lookup.service';
import {NgEventBus} from 'ng-event-bus';
import {PlanModelService} from '../common/service/plan-model.service';

import {FormsModule, NgForm} from '@angular/forms';
import {PermissionsService} from '../common/service/permissions.service';
import {PlanLodgementService} from '../common/service/plan-lodgement.service';
import {NlrPermissionsDisabledPipe} from '../common/pipe/nlr-permissions-disabled-pipe';
import {NlrPlanDetailService} from '../plan-detail/plan-detail.service';
import {map, switchMap} from 'rxjs/operators';
import {NotificationAlertsService} from '../common/notification-alerts/notification-alerts.service';
import {AlertModalService, ConfirmationModalService} from '../common/component/modal';
import {catchError} from 'rxjs';
import {Router} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'nlr-interests',
    imports: [
        TenureInterestsNotificationsComponent,
        NewInterestsNotificationsComponent,
        AdditionalInterestNotificationQuestionsComponent,
        FormsModule,
        NlrPermissionsDisabledPipe
    ],
    templateUrl: './interests.component.html',
    styleUrl: './interests.component.scss'
})
export class InterestsComponent implements OnInit {
    @ViewChild('interestsForm')
    interestsForm: NgForm;

    protected readonly title = TABS.INTERESTS;

    interestsUiConfig: unknown;
    newInterestsUiConfig: unknown;
    newInterestsQuestionDisplay: unknown;

    constructor(
        private destroyRef: DestroyRef,
        private lookupService: LookupService,
        private eventBus: NgEventBus,
        private planModelService: PlanModelService,
        private planLodgementService: PlanLodgementService,
        private permissionsService: PermissionsService,
        private nlrPlanDetailService: NlrPlanDetailService,
        private notificationAlertsService: NotificationAlertsService,
        private alertModalService: AlertModalService,
        private confirmationModalService: ConfirmationModalService,
        private router: Router
    ) {}

    handlePlanPurposeOrPlanTypeChange() {
        const {planPurpose, pdfCreationMethod} = this.planModelService.getModel();
        if (planPurpose) {
            const planPurposeLookup = this.lookupService.getLookups('planPurpose').find(v => v.id === planPurpose.id);

            this.interestsUiConfig = this.lookupService
                .getLookups('interestsUiConfig')
                .find(
                    l =>
                        l.pdfCreationMethod === pdfCreationMethod &&
                        l.parentTenureAssociationType === planPurposeLookup.parentTenureAssociationType &&
                        l.broughtForwardNotificationsGroupDisplay
                );

            this.newInterestsUiConfig = this.lookupService
                .getLookups('interestsUiConfig')
                .find(
                    l =>
                        l.pdfCreationMethod === pdfCreationMethod &&
                        l.parentTenureAssociationType === planPurposeLookup.parentTenureAssociationType &&
                        l.newInterestsTableDisplay
                );
            console.log(this.newInterestsUiConfig);
            this.newInterestsQuestionDisplay = this.lookupService
                .getLookups('interestsUiConfig')
                .find(
                    l =>
                        l.pdfCreationMethod === pdfCreationMethod &&
                        l.parentTenureAssociationType === planPurposeLookup.parentTenureAssociationType &&
                        l.newInterestSameOwner
                );
        } else {
            delete this.interestsUiConfig;
        }
    }

    ngOnInit() {
        this.eventBus
            .on('nlr:plan-model-updated:*')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.handlePlanPurposeOrPlanTypeChange());
        this.handlePlanPurposeOrPlanTypeChange();

        this.eventBus
            .on('SAVE_INTERESTS')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.save());

        this.eventBus
            .on<boolean>('VALIDATE_INTERESTS')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(meta => this.validate(meta.data));
    }

    showTenureInterestsNotifications(): boolean {
        return this.interestsUiConfig !== undefined;
    }

    save() {
        console.debug(this.title + ' controller save');

        if (!this.permissionsService.hasEditPermission(['EDIT_INTERESTS', 'VIEW_INTERESTS']) || this.saveDisabled()) {
            return;
        }

        const obs = this.planLodgementService.save();
        this.eventBus.cast('nlr:busy', obs.subscribe());
    }

    saveDisabled() {
        return this.hasInvalidPlanDetails() || this.planModelService.readonly;
    }

    hasInvalidPlanDetails() {
        return !this.planLodgementService.hasMinimumPlanDetails();
    }

    validateDisabled() {
        return this.hasInvalidPlanDetails() || this.planModelService.readonly;
    }

    validate(skipSave = false) {
        console.debug(this.title + ' controller validate');

        if (
            !this.permissionsService.hasEditPermission(['EDIT_PLAN', 'VIEW_PLAN']) ||
            this.validateDisabled() ||
            !this.planLodgementService.interestsApplicable()
        ) {
            return;
        }

        // put the form in a submitted state so required-field errors stay visible after the save below replaces the
        // plan model - controls recreated against the new model are pristine again, but submitted is held on the NgForm
        this.interestsForm.onSubmit(undefined);
        this.markAllFormsAsDirty();

        const handleAlerts = () => {
            if (this.formInvalid()) {
                if (!this.notificationAlertsService.hasMessageType('InterestForm')) {
                    this.notificationAlertsService.addMessage({
                        text: 'The Interests page is incomplete or data entered is invalid!',
                        subType: 'InterestForm'
                    });
                }
            }
            if (
                this.nlrPlanDetailService
                    .getValidationResults()
                    .getValue()
                    .some(
                        v =>
                            (v.type === RULE_OUTCOME.CRITICAL || v.type === RULE_OUTCOME.ERROR) &&
                            v.stage === PLAN_VALIDATION_STAGE.LODGEMENT_INTERESTS
                    )
            ) {
                if (!this.notificationAlertsService.hasMessageType('InterestRules')) {
                    this.notificationAlertsService.addMessage({text: 'The Interests page has business rule errors!', subType: 'InterestRules'});
                }
            }
        };

        if (this.planLodgementService.hasMinimumPlanDetails()) {
            if (this.planModelService.readonly || skipSave) {
                const obs = this.nlrPlanDetailService.runValidationsForStage(PLAN_VALIDATION_STAGE.LODGEMENT_INTERESTS).pipe(
                    map(handleAlerts),
                    catchError(reason => this.alertModalService.createAlertMessage(reason))
                );
                this.eventBus.cast('nlr:busy', obs.subscribe());
            } else {
                this.planLodgementService.increaseValidationAttempt();

                const obs = this.planModelService.save().pipe(
                    switchMap(() => this.nlrPlanDetailService.runValidationsForStage(PLAN_VALIDATION_STAGE.LODGEMENT_INTERESTS)),
                    map(handleAlerts),
                    catchError(reason => this.alertModalService.createAlertMessage(reason))
                );
                this.eventBus.cast('nlr:busy', obs.subscribe());
            }
        }
    }

    markAllFormsAsDirty() {
        Object.keys(this.interestsForm.controls).forEach(controlName => {
            const control = this.interestsForm.controls[controlName];
            control.markAsDirty();
        });
    }

    formInvalid() {
        let invalid = false;
        Object.keys(this.interestsForm.controls).forEach(controlName => {
            const control = this.interestsForm.controls[controlName];
            if (control.invalid) {
                invalid = true;
            }
        });
        return invalid;
    }

    cancel() {
        console.debug(this.title + ' controller cancel');
        if (this.planModelService.modelIsDirty()) {
            this.confirmationModalService
                .showModal({
                    title: 'Warning',
                    confirmationText: 'You are about to lose all changes. Are you sure you wish to continue?'
                })
                .subscribe((action: string) => {
                    if (action === 'yes') {
                        this.router.navigate(['/plan-dashboard']);
                    }
                });
        } else {
            this.router.navigate(['/plan-dashboard']);
        }
    }

    readonly() {
        return this.planModelService.readonly;
    }
}
